import {useCallback} from 'react';
import {useSelector} from 'react-redux';

const RECAPTCHA_API = 'https://www.google.com/recaptcha/api.js?render=explicit';
const RECAPTCHA_SCRIPT_ID = 'google-recaptcha-v2-script';
const RECAPTCHA_CONTAINER_ID = 'google-recaptcha-v2-container';
const RECAPTCHA_TIMEOUT_MS = 8000;

let scriptPromise = null;
let widgetId = null;
let widgetSiteKey = null;
let pendingResolve = null;
let pendingReject = null;
let pendingTimeoutId = null;
let challengeObserver = null;

const stopChallengeObserver = () => {
    if (challengeObserver) {
        challengeObserver.disconnect();
        challengeObserver = null;
    }
};

const resetWidgetSafe = () => {
    if (widgetId !== null && window.grecaptcha) {
        try {
            window.grecaptcha.reset(widgetId);
        } catch (e) { /* ignore */ }
    }
};

const clearPendingPromise = () => {
    pendingResolve = null;
    pendingReject = null;
    if (pendingTimeoutId !== null) {
        clearTimeout(pendingTimeoutId);
        pendingTimeoutId = null;
    }
    stopChallengeObserver();
};

const resolvePendingPromise = token => {
    if (typeof pendingResolve === 'function') {
        pendingResolve(token);
    }
    clearPendingPromise();
};

const rejectPendingPromise = error => {
    if (typeof pendingReject === 'function') {
        pendingReject(error);
    }
    clearPendingPromise();
};

const ensureScriptLoaded = () => {
    if (window.grecaptcha) {
        return Promise.resolve();
    }

    if (scriptPromise) {
        return scriptPromise;
    }

    scriptPromise = new Promise((resolve, reject) => {
        const currentScript = document.getElementById(RECAPTCHA_SCRIPT_ID);
        if (currentScript) {
            currentScript.addEventListener('load', () => resolve());
            currentScript.addEventListener('error', () => reject(new Error('Failed to load reCAPTCHA')));
            return;
        }

        const script = document.createElement('script');
        script.id = RECAPTCHA_SCRIPT_ID;
        script.src = RECAPTCHA_API;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load reCAPTCHA'));

        document.body.appendChild(script);
    });

    return scriptPromise;
};

const ensureContainer = () => {
    let container = document.getElementById(RECAPTCHA_CONTAINER_ID);
    if (container) {
        return container;
    }

    container = document.createElement('div');
    container.id = RECAPTCHA_CONTAINER_ID;
    container.style.cssText = 'position:absolute;top:-9999px;left:-9999px;width:0;height:0;overflow:hidden;';
    document.body.appendChild(container);

    return container;
};

const ensureWidget = siteKey => {
    if (!window.grecaptcha) {
        throw new Error('grecaptcha is not available');
    }

    if (widgetId !== null && widgetSiteKey === siteKey) {
        return widgetId;
    }

    const container = ensureContainer();
    container.innerHTML = '';

    widgetSiteKey = siteKey;
    widgetId = window.grecaptcha.render(container, {
        sitekey: siteKey,
        size: 'invisible',
        callback: token => {
            resolvePendingPromise(token);
            if (widgetId !== null) {
                window.grecaptcha.reset(widgetId);
            }
        },
        'error-callback': () => {
            rejectPendingPromise(new Error('reCAPTCHA error'));
            if (widgetId !== null) {
                window.grecaptcha.reset(widgetId);
            }
        },
        'expired-callback': () => {
            rejectPendingPromise(new Error('reCAPTCHA expired'));
            if (widgetId !== null) {
                window.grecaptcha.reset(widgetId);
            }
        },
    });

    return widgetId;
};

const observeChallengeClose = () => {
    stopChallengeObserver();

    const findChallengeContainer = () => {
        const iframe = document.querySelector('iframe[src*="/bframe"]');
        if (!iframe) return null;
        let el = iframe;
        while (el.parentElement && el.parentElement !== document.body) {
            el = el.parentElement;
        }
        return el;
    };

    const checkClosed = () => {
        const container = findChallengeContainer();
        if (container && container.style.visibility === 'hidden' && pendingReject) {
            rejectPendingPromise(new Error('reCAPTCHA challenge closed'));
        }
    };

    challengeObserver = new MutationObserver(checkClosed);
    challengeObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['style'],
        subtree: true,
    });
};

const waitForReady = () => new Promise(resolve => {
    if (!window.grecaptcha || typeof window.grecaptcha.ready !== 'function') {
        resolve();
        return;
    }

    window.grecaptcha.ready(resolve);
});

function useInvisibleRecaptcha() {
    const {recaptchaSiteKey} = useSelector(state => state.site);

    const execute = useCallback(async () => {
        if (!recaptchaSiteKey) {
            return null;
        }

        try {
            await ensureScriptLoaded();
            await waitForReady();

            const currentWidgetId = ensureWidget(recaptchaSiteKey);

            return await new Promise((resolve, reject) => {
                if (typeof pendingReject === 'function') {
                    pendingReject(new Error('Interrupted by a new captcha request'));
                }

                pendingResolve = resolve;
                pendingReject = reject;
                pendingTimeoutId = setTimeout(() => {
                    rejectPendingPromise(new Error('reCAPTCHA timeout'));
                    resetWidgetSafe();
                }, RECAPTCHA_TIMEOUT_MS);
                window.grecaptcha.execute(currentWidgetId);
                observeChallengeClose();
            });
        } catch (e) {
            return null;
        }
    }, [recaptchaSiteKey]);

    return {
        execute,
        enabled: !!recaptchaSiteKey,
    };
}

export default useInvisibleRecaptcha;
